import { Box, CircularProgress, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { AiOutlineWarning } from 'react-icons/ai';
import { MdCheckCircleOutline, MdClose, MdInfoOutline } from 'react-icons/md';

interface ComponentProps {
  onClose: any;
  helperText?: string;
  heading?: string;
  subHeading?: string;
  status: 'pending' | 'success' | 'failed';
}

export default function ConfigurationLoaderBadge({ onClose, status, helperText, heading, subHeading }: ComponentProps) {
  const theme = useTheme();
  const isFailed = status === 'failed';
  const isPending = status === 'pending';
  const statusConfig = getStatusConfigs(status, theme);
  const displayHeading = heading || statusConfig.heading;
  const displaySubHeading = subHeading || statusConfig.subHeading;
  const { borderColor, bgColor, icon } = statusConfig;

  return (
    <Stack spacing={1} mb={2}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        sx={{
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          p: '12px 16px'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: 1 }}>
          {icon}
          <Box>
            <Typography fontWeight={600} fontSize={14} color={isFailed ? 'error.main' : 'text.primary'}>
              {displayHeading}
            </Typography>
            <Typography fontSize={12} color={isFailed ? 'error.main' : 'text.secondary'} mt={0.5} lineHeight={1.4}>
              {displaySubHeading}
            </Typography>
          </Box>
        </Box>
        {!isPending && (
          <IconButton onClick={onClose} size='small' sx={{ mt: -0.5, mr: -0.5 }}>
            <MdClose color={isFailed ? theme.palette.error.main : theme.palette.text.secondary} size={18} />
          </IconButton>
        )}
      </Stack>
      {isPending && helperText && (
        <Box display='flex' alignItems='center' gap={0.5}>
          <MdInfoOutline size={14} color={theme.palette.text.secondary} />
          <Typography fontSize={12} color='text.secondary' lineHeight={1.4}>
            {helperText}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

const getStatusConfigs = (status: 'success' | 'pending' | 'failed', theme: Theme) => {
  const blueMain = theme.palette.primary.main;
  const blueLight = theme.palette.primary.light;
  const blueBorder = alpha(blueMain, 0.2);

  switch (status) {
    case 'failed':
      return {
        heading: '401 — Authentication failed',
        subHeading: 'Integration authentication failed. Please check your credentials and update.',
        icon: (
          <Box sx={{ mt: 0.25, flexShrink: 0 }}>
            <AiOutlineWarning size={18} color={theme.palette.error.main} />
          </Box>
        ),
        borderColor: theme.palette.error.main,
        bgColor: theme.palette.error.light
      };
    case 'pending':
      return {
        heading: 'Validating Your Credentials',
        subHeading: 'We are currently validating your API credentials and configuring the integration.',
        icon: (
          <Box sx={{ position: 'relative', display: 'inline-flex', mt: 0.25, flexShrink: 0 }}>
            <CircularProgress
              variant='determinate'
              value={100}
              size={18}
              thickness={4}
              sx={{ color: blueMain, opacity: 0.2 }}
            />
            <CircularProgress size={18} thickness={4} sx={{ color: blueMain, position: 'absolute', left: 0 }} />
          </Box>
        ),
        borderColor: blueBorder,
        bgColor: blueLight
      };
    default:
      return {
        heading: 'Integrations Reviewed & Verified Successfully',
        subHeading: 'The integration has been successfully verified and configured.',
        icon: (
          <Box sx={{ mt: 0.25, flexShrink: 0 }}>
            <MdCheckCircleOutline size={20} color={blueMain} />
          </Box>
        ),
        borderColor: blueBorder,
        bgColor: blueLight
      };
  }
};
